import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useHeaderMobileDetect from "@tBrasseler/hooks/UseHeaderMobileDetect";
import React, { useState } from "react";
import { css } from "styled-components";

const enum fields {
    logo = "logo",
    hoverLogo = "hoverLogo",
    description = "description",
    btnTitle = "btnTitle",
    destination = "destination",
    image = "image",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.logo]: string;
        [fields.hoverLogo]: string;
        [fields.description]: string;
        [fields.btnTitle]: string;
        [fields.destination]: LinkFieldValue;
        [fields.image]: string;
    };
}

export interface HeaderNavigationStyles {
    firstNavigationWrapper?: InjectableCss;
    firstNavigationItemsContainerWrapper?: InjectableCss;
    firstNavigationItemsContainer?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    hoverLogo?: InjectableCss;
    description?: TypographyProps;
    title?: TypographyProps;
    rightContent?: InjectableCss;
    iconButton?: InjectableCss;
    navWrapperDesktop?: InjectableCss;
    navZone?: InjectableCss;
    signInButton?: InjectableCss;
    searchWrapperDesktop?: InjectableCss;
    container?: InjectableCss;
    logoZone?: InjectableCss;
    desktopWrapper?: InjectableCss;
}

const headerNavigationStyles: HeaderNavigationStyles = {
    firstNavigationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: end;
            background: linear-gradient(
                90deg,
                rgb(0, 26, 138) 0%,
                rgb(0, 70, 186) 42%,
                rgb(0, 104, 200) 75%,
                rgb(0, 159, 223) 100%
            );
        `,
    },
    firstNavigationItemsContainer: {
        css: css`
            display: flex;
            gap: 15px;
            font-weight: 600;
            color: white;
        `,
    },

    firstNavigationItemsContainerWrapper: {
        css: css`
            width: 80%;
            margin: auto;
            display: flex;
            justify-content: end;
            font-size: 12px;
            padding: 5px 0px;
        `,
    },

    secondaryNavigationWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    iconButton: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
        `,
    },
    rightContent: {
        css: css`
            display: flex;
            align-items: center;
            flex: 1; /* 🔥 allows search to grow */
            justify-content: flex-end;
            position: relative;
            min-width: 0;
        `,
    },
    navWrapperDesktop: {
        css: css`
            display: flex;
            align-items: center;
            gap: 20px;
        `,
    },
    navZone: {
        css: css`
            display: flex;
            align-items: center;
            gap: 16px;
            flex: 1;
        `,
    },
    signInButton: {
        css: css`
            white-space: nowrap;
            padding: 0 !important;
            border-radius: 5px;

            transition: 0.3s;
            padding: 8px 18px 8px 18px !important;
            span {
                font-weight: 500;
                font-size: 14px;
            }
        `,
    },
    searchWrapperDesktop: {
        css: css`
            display: flex;
            align-items: center;
            gap: 12px;
            width: 80%;
        `,
    },
    container: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 80%;
            margin: auto;
            position: relative;
            padding: 15px 0px;
        `,
    },

    /* 🔹 LOGO */
    logoZone: {
        css: css`
            width: 28%;
            max-width: 334px;
            display: flex;
            align-items: center;
        `,
    },

    /* 🔹 RIGHT SIDE WRAPPER (desktop) */
    desktopWrapper: {
        css: css`
            flex: 1 1 auto;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            min-width: 0;
        `,
    },
};

const styles = headerNavigationStyles;
type Props = OwnProps & PageProps;

const HeaderNavigation: React.FunctionComponent<Props> = ({ id }) => {
    const [isSearchEnabled, setSearchEnabled] = useState<boolean>(false);
    const { isMobileHeader } = useHeaderMobileDetect();
    const toggleSearch = () => {
        setSearchEnabled(prev => !prev);
    };
    return (
        <StyledWrapper>
            {/**first navigation */}
            {!isMobileHeader && (
                <StyledWrapper {...styles.firstNavigationWrapper}>
                    <StyledWrapper {...styles.firstNavigationItemsContainerWrapper}>
                        <StyledWrapper {...styles.firstNavigationItemsContainer}>
                            <StyledWrapper>Corporate</StyledWrapper>
                            <StyledWrapper>Dental</StyledWrapper>
                            <StyledWrapper>Surgical</StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {/**second navigation */}
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.logoZone}>
                    <Zone contentId={id} zoneName="Logo" />
                </StyledWrapper>
                <StyledWrapper {...styles.desktopWrapper}>
                    <StyledWrapper {...styles.rightContent}>
                        {/* NAV */}
                        {!isSearchEnabled ? (
                            <StyledWrapper {...styles.navWrapperDesktop}>
                                {isMobileHeader && (
                                    <StyledWrapper
                                        {...styles.iconButton}
                                        onClick={toggleSearch}
                                        aria-label="Open search"
                                    >
                                        <Search />
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.navZone}>
                                    <Zone contentId={id} zoneName="MainNavigation" />
                                </StyledWrapper>

                                {!isMobileHeader && <Button css={styles.signInButton?.css}>Sign In</Button>}

                                {!isMobileHeader && (
                                    <StyledWrapper
                                        {...styles.iconButton}
                                        onClick={toggleSearch}
                                        aria-label="Open search"
                                    >
                                        <Search />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        ) : (
                            <StyledWrapper {...styles.searchWrapperDesktop}>
                                <input
                                    placeholder="Search"
                                    style={{
                                        width: "100%",
                                        border: "none",
                                        outline: "none",
                                        padding: "8px 12px",
                                        fontSize: "14px",
                                    }}
                                />

                                <StyledWrapper {...styles.iconButton} onClick={toggleSearch} aria-label="Close search">
                                    <X />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}

                        {/* SEARCH */}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};
const headerNavigation: WidgetModule = {
    component: HeaderNavigation,
    definition: {
        group: "Header",
        allowedContexts: ["Header"],
        icon: "magnifying-glass",
        displayName: "Custom Header",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.logo,
                editorTemplate: "ImagePickerField",
                displayName: "Logo",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.hoverLogo,
                editorTemplate: "ImagePickerField",
                displayName: "Hover Logo",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.description,
                editorTemplate: "TextField",
                displayName: "Description",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.btnTitle,
                editorTemplate: "TextField",
                displayName: "Button Text",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.destination,
                editorTemplate: "LinkField",
                displayName: "Destination",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                displayName: "Main Image",
                defaultValue: "",
            },
        ],
    },
};

export default headerNavigation;
